import type {
  DirectoryAccount,
  DirectoryAccountDetailsDTO,
  EditKind,
  EditRequestDTO,
} from '@/modules/projects/stores/types'

// What the confirmation asks, and what its button says, for each kind of edit.
export const EDIT_QUESTIONS: Record<
  EditKind,
  { title: string; confirm: string }
> = {
  refile_statement: {
    title: 'Move this statement to another account?',
    confirm: 'Move statement',
  },
  change_statement_period: {
    title: "Change this statement's dates?",
    confirm: 'Change dates',
  },
  edit_account: { title: 'Change this account?', confirm: 'Change account' },
  add_account: { title: 'Add this account?', confirm: 'Add account' },
  remove_account: { title: 'Remove this account?', confirm: 'Remove account' },
  recut_file: { title: 'Apply these page moves?', confirm: 'Apply' },
  move_pages: { title: 'Move these pages?', confirm: 'Move pages' },
  take_out_pages: {
    title: 'Take these pages out of the statement?',
    confirm: 'Take out',
  },
  start_statement: {
    title: 'Start a statement from these pages?',
    confirm: 'Start statement',
  },
}

type AccountFields = keyof Omit<DirectoryAccountDetailsDTO, 'last4'>
const ACCOUNT_FIELDS: AccountFields[] = ['account_type', 'owner', 'institution']

const entryValue = (entry: DirectoryAccount, field: AccountFields) =>
  ({
    account_type: entry.accountType,
    owner: entry.owner,
    institution: entry.institution,
  })[field] ?? null

// Only the fields that really change are sent: the server refuses an edit that changes nothing, and
// a lane edit touches one field while the lane keeps all four.
const changedFields = (
  entry: DirectoryAccount,
  body: DirectoryAccountDetailsDTO,
): Partial<DirectoryAccountDetailsDTO> => {
  const changed: Partial<DirectoryAccountDetailsDTO> = {}
  if (body.last4 !== entry.last4) changed.last4 = body.last4
  for (const field of ACCOUNT_FIELDS) {
    if ((body[field] ?? null) !== entryValue(entry, field))
      changed[field] = body[field] ?? null
  }
  return changed
}

// One lane edit becomes one edit per number it holds: a combined mailing keeps a directory entry per
// number, and a number the directory never had is added.
export const accountEditRequests = (
  entriesByNumber: (last4: string) => DirectoryAccount | undefined,
  oldNumbers: string[],
  bodies: DirectoryAccountDetailsDTO[],
): EditRequestDTO[] =>
  bodies.flatMap((body, index): EditRequestDTO[] => {
    const entry = entriesByNumber(oldNumbers[index] ?? body.last4)
    if (!entry) return [{ kind: 'add_account', account: body }]

    const account = changedFields(entry, body)
    return Object.keys(account).length === 0
      ? []
      : [{ kind: 'edit_account', account_id: entry.id, account }]
  })
